import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, IndianRupee, Globe2 } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Seo, faqLd } from '../components/Seo';
import { Section, Container, SectionHeading, Eyebrow } from '../components/marketing/Section';
import { PrimaryButton, SecondaryButton } from '../components/marketing/Buttons';
import { ProductMockup } from '../components/marketing/ProductMockup';
import { TrustStats } from '../components/marketing/TrustStats';
import { HowItWorks } from '../components/marketing/HowItWorks';
import { BeforeAfter } from '../components/marketing/BeforeAfter';
import { JobMatch } from '../components/marketing/JobMatch';
import { TemplateGallery } from '../components/marketing/TemplateCard';
import { FeatureCard } from '../components/marketing/FeatureCard';
import { PricingPlans } from '../components/marketing/PricingPlans';
import { FAQAccordion } from '../components/marketing/FAQAccordion';
import { FinalCTA } from '../components/marketing/FinalCTA';
import { ThemeShowcase } from '../components/marketing/ThemeShowcase';
import { StickyMobileCTA } from '../components/marketing/StickyMobileCTA';
import { useStartResume } from '../hooks/useStartResume';
import api from '../lib/api';
import { track } from '../lib/analytics';
import { SITE_URL, SUPPORT_EMAIL } from '../lib/site';
import { FEATURES, FAQS, HOME_FAQ_INDEXES, CAREER_STAGES, SIGNUP_PATH } from '../lib/marketing';
import { useCurrency } from '../context/CurrencyContext';
import { priceOffers } from '../lib/pricing';

const HOME_FAQS = HOME_FAQ_INDEXES.map((i) => FAQS[i]);
const JSONLD = [
  { '@type': 'Organization', name: 'Careerova', legalName: 'Careerova Technologies LLP', url: SITE_URL, logo: `${SITE_URL}/logo512.png`, email: SUPPORT_EMAIL },
  { '@type': 'WebSite', name: 'Careerova', url: SITE_URL },
  { '@type': 'SoftwareApplication', name: 'Careerova', applicationCategory: 'BusinessApplication', operatingSystem: 'Web', url: SITE_URL,
    description: 'AI resume builder with ATS-friendly templates, AI writing help, job-description matching, PDF export and 19 languages.' },
  faqLd(HOME_FAQS),
];

export default function Landing() {
  const [templates, setTemplates] = useState([]);
  const { start } = useStartResume();
  const { plans, currency, loading } = useCurrency();
  const jsonLd = JSONLD.map((item) => item['@type'] === 'SoftwareApplication'
    ? { ...item, offers: loading ? [] : priceOffers(plans, currency, `${SITE_URL}/pricing`) } : item);
  useEffect(() => { api.get('/templates').then(({ data }) => setTemplates(data)).catch((e) => console.error('templates load failed', e)); }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Seo title="Careerova | AI Resume Builder & CV Maker" path="/" jsonLd={jsonLd}
        description="Careerova is an AI resume builder for India and worldwide: ATS-friendly templates, AI writing help, job-description matching and clean PDF export in 19 languages. Start free." />
      <Header showNavLinks />

      <main id="main">
        {/* Hero */}
        <section className="hero-wash relative overflow-hidden pt-8 sm:pt-20">
          <Container>
            <div className="mx-auto max-w-3xl text-center">
              <Eyebrow>AI resume builder · India & worldwide</Eyebrow>
              <h1 className="mt-4 text-[36px] font-bold leading-[1.05] text-primary sm:text-[56px] lg:text-[72px]">Build a resume you can actually send.</h1>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:mt-6 sm:text-[19px]">Create an ATS-friendly resume in minutes, tailor it to any job, improve your content with AI, and export a polished PDF that's ready to apply with.</p>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:mt-8 sm:flex-row">
                <PrimaryButton to={SIGNUP_PATH} arrow data-testid="hero-cta-button" onClick={() => track('hero_cta_click', { placement: 'hero' })} className="w-full sm:w-auto">Build My Resume Free</PrimaryButton>
                <SecondaryButton to="/templates" data-testid="hero-secondary-button" className="w-full sm:w-auto">Explore Templates</SecondaryButton>
              </div>
              <p className="mt-4 text-xs text-muted-foreground sm:mt-5 sm:text-sm">No card required · Start free · Pay only when you need premium access</p>
            </div>
            <div id="product" className="relative mt-8 scroll-mt-24 sm:mt-16">
              <div className="grid-lines absolute inset-x-0 -top-10 -z-10 h-[420px]" aria-hidden="true" />
              <ProductMockup />
            </div>
          </Container>
        </section>

        {/* Product facts */}
        <Section className="!pt-12 sm:!pt-16"><TrustStats /></Section>

        {/* How it works */}
        <Section id="how" className="border-t border-border">
          <SectionHeading eyebrow="How Careerova works" title="From blank page to job-ready in minutes." desc="One flow, four steps. Import what you have, target the role, improve the content, export a PDF." />
          <div className="mt-12"><HowItWorks /></div>
          <Link to="/how-it-works" data-testid="home-how-link" className="mt-8 inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-accent hover:underline">See the full process <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </Section>

        {/* Before / After */}
        <Section className="border-t border-border bg-surface/60">
          <SectionHeading eyebrow="AI writing" title="See what Careerova AI actually changes." desc="The AI rewrites for clarity, specificity and impact — it never invents experience. Every suggestion is yours to review before it goes into your resume." />
          <div className="mt-12"><BeforeAfter /></div>
        </Section>

        {/* Job match */}
        <Section className="border-t border-border">
          <div className="grid items-start gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <SectionHeading eyebrow="Job description matching" title="Tailor your resume to the job." desc="Paste a job description and Careerova helps identify relevant keywords, missing skills and areas where your resume can be strengthened." />
              <ul className="mt-6 space-y-3 text-[15px] text-primary/85">
                {['Resume match score and keyword alignment for the specific role', 'Matched and missing keywords, taken from the job description you paste', 'Actionable suggestions you can apply in the editor'].map((s) => <li key={s} className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />{s}</li>)}
              </ul>
              <PrimaryButton to={SIGNUP_PATH} arrow data-testid="jobmatch-cta" className="mt-8" onClick={() => track('hero_cta_click', { placement: 'job_match' })}>Analyze My Job Match</PrimaryButton>
            </div>
            <JobMatch />
          </div>
        </Section>

        {/* Templates */}
        <Section id="templates" className="border-t border-border bg-surface/60">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading eyebrow="Templates" title="Templates that look good and parse cleanly." desc="Choose a layout that fits your role without sacrificing readability." />
            <Link to="/templates" data-testid="home-view-all-templates" className="inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-accent hover:underline">View all templates <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          </div>
          <div className="mt-12"><TemplateGallery templates={templates.slice(0, 6)} onUse={start} compact /></div>
        </Section>

        {/* Themes */}
        <Section className="border-t border-border">
          <SectionHeading eyebrow="Colour themes" title="Professional colour themes, previewed live." desc="Pick an accent and see the template update instantly." />
          <div className="mt-12"><ThemeShowcase /></div>
        </Section>

        {/* AI features */}
        <Section id="features" className="border-t border-border bg-surface/60">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading eyebrow="AI writing features" title="Writing help that stays honest." desc="Draft, rewrite and strengthen — with suggestions you review before use." />
            <Link to="/features" data-testid="home-view-all-features" className="inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-accent hover:underline">All features <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.filter((f) => ['writer', 'bullets', 'rewrite', 'skills'].includes(f.kind)).map((f) => <FeatureCard key={f.id} feature={f} showCta={false} />)}
          </div>
        </Section>

        {/* Career stages */}
        <Section className="border-t border-border">
          <SectionHeading eyebrow="Who it's for" title="Built for every stage of your career." desc="From a first internship to a senior move — the templates and guidance adapt to where you are." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CAREER_STAGES.map((c) => (
              <div key={c.title} className="rounded-2xl border border-border bg-white p-6">
                <h3 className="text-lg font-bold text-primary">{c.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{c.desc}</p>
                <p className="mt-4 text-xs text-muted-foreground">Suggested: {c.tags.join(', ')}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* India + Global */}
        <Section className="border-t border-border bg-surface/60">
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-white p-8">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent"><IndianRupee className="h-5 w-5" aria-hidden="true" /></span>
              <h3 className="mt-4 text-2xl font-bold text-primary">Made for India</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">11 Indian regional languages, an India-first template with optional photo, INR pricing and UPI, cards and net banking via Razorpay.</p>
              <Link to="/resume-builder-india" data-testid="home-india-link" className="mt-5 inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-accent hover:underline">Resume Builder India <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
            </div>
            <div className="rounded-2xl border border-border bg-white p-8">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent"><Globe2 className="h-5 w-5" aria-hidden="true" /></span>
              <h3 className="mt-4 text-2xl font-bold text-primary">Ready for the world</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">19 languages including Spanish, French, German, Portuguese, Arabic, Chinese and Japanese. Pay by card in USD. Resume conventions vary by country — templates keep photos and personal details optional.</p>
            </div>
          </div>
        </Section>

        {/* Pricing */}
        <Section id="pricing" className="border-t border-border">
          <SectionHeading align="center" eyebrow="Pricing" title="Simple pricing. No subscription traps." desc="Build your resume for free. Pay only when you need premium access." />
          <div className="mx-auto mt-12 max-w-3xl"><PricingPlans /></div>
          <p className="mt-6 text-center text-sm text-muted-foreground">One-time payment · No subscription · No auto-renewal · <Link to="/pricing" data-testid="landing-view-pricing" className="font-semibold text-accent hover:underline">How billing works</Link></p>
        </Section>

        {/* FAQ */}
        <Section id="faq" className="border-t border-border bg-surface/60">
          <div className="mx-auto max-w-3xl">
            <SectionHeading align="center" eyebrow="FAQ" title="Questions, answered." />
            <div className="mt-10"><FAQAccordion faqs={HOME_FAQS} /></div>
            <p className="mt-6 text-center text-sm text-muted-foreground"><Link to="/faq" data-testid="home-faq-link" className="font-semibold text-accent hover:underline">See all questions →</Link></p>
          </div>
        </Section>

        <FinalCTA />
      </main>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
